import React, { useState, useRef } from 'react';
import './uid.css';

const CryptoSurvey = () => {
    const [formData, setFormData] = useState({
        bingxUID: '',
        telegramUsername: '',
        cryptoNetWorth: '',
        image: null
    });
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({
        bingxUID: false,
        telegramUsername: false,
        cryptoNetWorth: false,
        image: false
    });
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const fileInputRef = useRef(null);

    const netWorthOptions = [
        'Less than $1,000',
        '$1,000 - $10,000',
        '$10,000 - $50,000',
        '$50,000 - $100,000',
        '$100,000 - $500,000',
        '$500,000 - $1,000,000',
        'More than $1,000,000'
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: false
            }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));
            setPreviewImage(URL.createObjectURL(file));
            if (errors.image) {
                setErrors(prev => ({
                    ...prev,
                    image: false
                }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {
            bingxUID: !formData.bingxUID.trim(),
            telegramUsername: !formData.telegramUsername.trim(),
            cryptoNetWorth: !formData.cryptoNetWorth,
            image: !formData.image
        };
        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const submitSurvey = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setSubmitError('Please fill out all required fields');
            return;
        }

        setIsLoading(true);
        setSubmitError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('bingxUID', formData.bingxUID);
            formDataToSend.append('telegramUsername', formData.telegramUsername);
            formDataToSend.append('cryptoNetWorth', formData.cryptoNetWorth);
            formDataToSend.append('image', formData.image);

            const response = await fetch('https://www.cryptoxnft.org/api/survey', {
                method: 'POST',
                body: formDataToSend
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.details || data.error || 'Failed to submit survey');
            }

            setSubmitted(true);
        } catch (error) {
            console.error('Full submission error:', error);
            setSubmitError(error.message || 'An error occurred while submitting');
        } finally {
            setIsLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="survey-container">
                <div className="thank-you-message">
                    <h2>🎉 Thank You! 🎉</h2>
                    <p>Your survey has been submitted successfully.</p>
                    <p>You have entered our $10,000 giveaway!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="survey-container-wrapper">
            <div className="survey-container">
                <div className="survey-header">
                    <h1>Complete This Survey & Enter Our $10K Giveaway!</h1>
                    <p>Share your details for a chance to win big in crypto!</p>
                </div>
                <form onSubmit={submitSurvey} className="survey-form" encType="multipart/form-data">
                    <div className={`form-group ${errors.bingxUID ? 'error' : ''}`}>
                        <label htmlFor="bingxUID">BingX UID</label>
                        <input
                            type="text"
                            id="bingxUID"
                            name="bingxUID"
                            value={formData.bingxUID}
                            onChange={handleChange}
                            placeholder="Enter your BingX UID"
                        />
                        {errors.bingxUID && <span className="error-message">This field is required</span>}
                    </div>


                    <div className={`form-group ${errors.image ? 'error' : ''}`}>
                        <label htmlFor="image">Upload UID Verification Image *</label>
                        <input
                            type="file"
                            id="image"
                            name="image"
                            ref={fileInputRef}
                            onChange={handleImageChange}
                            accept="image/*"
                            style={{ display: 'none' }}
                        />
                        <div className="image-upload-container">
                            <button
                                type="button"
                                className="upload-button"
                                onClick={() => fileInputRef.current.click()}
                            >
                                {previewImage ? 'Change Image' : 'Select Image'}
                            </button>
                            {previewImage && (
                                <div className="image-preview">
                                    <img src={previewImage} alt="Preview" />
                                </div>
                            )}
                        </div>
                        {errors.image && <span className="error-message">Please upload an image</span>}
                        <p className="image-upload-note">Please upload a clear image for verification purposes</p>
                    </div>

                    <div className={`form-group ${errors.telegramUsername ? 'error' : ''}`}>
                        <label htmlFor="telegramUsername">Telegram Username</label>
                        <input
                            type="text"
                            id="telegramUsername"
                            name="telegramUsername"
                            value={formData.telegramUsername}
                            onChange={handleChange}
                            placeholder="@yourusername"
                        />
                        {errors.telegramUsername && <span className="error-message">This field is required</span>}
                    </div>
                    <div className={`form-group ${errors.cryptoNetWorth ? 'error' : ''}`}>
                        <label htmlFor="cryptoNetWorth">Your Crypto Net Worth</label>
                        <select
                            id="cryptoNetWorth"
                            name="cryptoNetWorth"
                            value={formData.cryptoNetWorth}
                            onChange={handleChange}
                        >
                            <option value="">Select your crypto net worth</option>
                            {netWorthOptions.map((option, index) => (
                                <option key={index} value={option}>{option}</option>
                            ))}
                        </select>
                        {errors.cryptoNetWorth && <span className="error-message">Please select your net worth range</span>}
                    </div>
                    {submitError && (
                        <div className="error-message server-error">
                            {submitError}
                        </div>
                    )}
                    <button
                        type="submit"
                        className="submit-button"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Submitting...' : 'Submit & Enter Giveaway'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default CryptoSurvey;